import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axiosInstance from '../utils/axiosConfig';
import { toast } from 'react-toastify';
import TrDateInput from '../components/TrDateInput';
import { formatDateTR } from '../utils/dateUtils';
import { useTranslation } from 'react-i18next';

// ─── Types ────────────────────────────────────────────────────────────────────
interface CalNote {
    notID: number;
    kullaniciID: number;
    notTarihi: string;
    notBaslik?: string;
    notIcerik?: string;
    notRengi: string;
    baslangicSaati?: string;
    bitisSaati?: string;
    aktifMi: boolean;
}

interface TodoGorev {
    todoID: number;
    kullaniciID: number;
    baslik: string;
    renk: string;
    tamamlandi: boolean;
    siraNo: number;
    gorevTarihi?: string;    // "YYYY-MM-DD"
    baslangicSaati?: string; // "HH:mm"
    bitisSaati?: string;     // "HH:mm"
}

type ViewType = 'month' | 'week' | 'day';

// ─── Constants (moved inside component — see Ajanda component body) ───────────

// Monday-based week helpers (Mon=0 … Sun=6)
function toDateStr(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
function trDayIndex(d: Date): number {
    return (d.getDay() + 6) % 7;
}
function getMondayOfWeek(d: Date): Date {
    const copy = new Date(d);
    copy.setHours(0, 0, 0, 0);
    copy.setDate(copy.getDate() - trDayIndex(copy));
    return copy;
}
function addDays(d: Date, n: number): Date {
    const copy = new Date(d);
    copy.setDate(copy.getDate() + n);
    return copy;
}

// ─── Time grid helpers ────────────────────────────────────────────────────────
const HOUR_HEIGHT = 60; // px per hour
const TIME_GUTTER = 52; // px, time-label column width

function parseTime(t: string): number {
    const [h, m] = t.split(':').map(Number);
    return h * 60 + (m || 0);
}
function minutesToStr(mins: number): string {
    return `${String(Math.floor(mins / 60)).padStart(2, '0')}:${String(mins % 60).padStart(2, '0')}`;
}
function snapTo30(mins: number): number {
    return Math.round(mins / 30) * 30;
}

// ─── Component ────────────────────────────────────────────────────────────────
const Ajanda: React.FC = () => {
    const { user } = useAuth();
    const { t } = useTranslation();

    const COLOR_OPTIONS = React.useMemo(() => [
        { value: 'primary',   label: 'Mavi',             bg: '#dbeafe', border: '#93c5fd', text: '#1e40af' },
        { value: 'success',   label: t('ajanda.yesil'),  bg: '#d1fae5', border: '#6ee7b7', text: '#065f46' },
        { value: 'danger',    label: t('ajanda.kirmizi'),bg: '#fde8e8', border: '#fca5a5', text: '#991b1b' },
        { value: 'warning',   label: t('ajanda.sari'),   bg: '#fef3c7', border: '#fcd34d', text: '#78350f' },
        { value: 'info',      label: t('ajanda.acikMavi'),bg: '#e0f2fe',border: '#7dd3fc', text: '#0c4a6e' },
        { value: 'secondary', label: 'Gri',              bg: '#f3f4f6', border: '#d1d5db', text: '#374151' },
    ], [t]);
    const getColor = (name: string) => COLOR_OPTIONS.find(c => c.value === name) ?? COLOR_OPTIONS[0];
    const TR_DAYS_SHORT = React.useMemo(() => [t('gun.pt'), t('gun.sa'), t('gun.ca'), t('gun.pe'), t('gun.cu'), t('gun.ct'), t('gun.pz')], [t]);
    const TR_DAYS_LONG  = React.useMemo(() => [t('gun.pazartesi'), t('gun.sali'), t('gun.carsamba'), t('gun.persembe'), t('gun.cuma'), t('gun.cumartesi'), t('gun.pazar')], [t]);
    const TR_MONTHS     = React.useMemo(() => [t('ay.ocak'), t('ay.subat'), t('ay.mart'), t('ay.nisan'), t('ay.mayis'), t('ay.haziran'),
                           t('ay.temmuz'), t('ay.agustos'), t('ay.eylul'), t('ay.ekim'), t('ay.kasim'), t('ay.aralik')], [t]);

    const todayBase = new Date();
    todayBase.setHours(0, 0, 0, 0);
    const todayStr = toDateStr(todayBase);

    // ── View & Navigation ─────────────────────────────────────────────────────
    const [view, setView] = useState<ViewType>('month');
    const [currentDate, setCurrentDate] = useState<Date>(new Date(todayBase));

    // ── Data ──────────────────────────────────────────────────────────────────
    const [notes, setNotes]     = useState<CalNote[]>([]);
    const [todos, setTodos]     = useState<TodoGorev[]>([]);
    const [loading, setLoading] = useState(false);

    // ── Refs ──────────────────────────────────────────────────────────────────
    const draggedNoteId = useRef<number | null>(null);
    const timeGridRef   = useRef<HTMLDivElement | null>(null);
    const [dragOverDate, setDragOverDate] = useState<string | null>(null);

    // ── Modal / Form ──────────────────────────────────────────────────────────
    const [showModal,          setShowModal]          = useState(false);
    const [editingNote,        setEditingNote]        = useState<CalNote | null>(null);
    const [formDate,           setFormDate]           = useState(todayStr);
    const [formBaslik,         setFormBaslik]         = useState('');
    const [formIcerik,         setFormIcerik]         = useState('');
    const [formRenk,           setFormRenk]           = useState('primary');
    const [formTumGun,         setFormTumGun]         = useState(true);
    const [formBaslangicSaati, setFormBaslangicSaati] = useState('');
    const [formBitisSaati,     setFormBitisSaati]     = useState('');
    const [saving,             setSaving]             = useState(false);

    // ── Todo add ──────────────────────────────────────────────────────────────
    const [yeniTodo,      setYeniTodo]      = useState('');
    const [yeniTodoTarih, setYeniTodoTarih] = useState('');
    const [yeniTodoSaat,  setYeniTodoSaat]  = useState('');
    const [yeniTodoBitis, setYeniTodoBitis] = useState('');
    const [todoAdding,    setTodoAdding]    = useState(false);
    // ── Todo edit modal ────────────────────────────────────────────────────────
    const [todoEditOpen,   setTodoEditOpen]   = useState(false);
    const [todoEditTarget, setTodoEditTarget] = useState<TodoGorev | null>(null);
    const [todoEditDate,   setTodoEditDate]   = useState('');
    const [todoEditSaat,   setTodoEditSaat]   = useState('');
    const [todoEditBitis,  setTodoEditBitis]  = useState('');
    const [todoEditSaving, setTodoEditSaving] = useState(false);

    // ─────────────────────────────────────────────────────────────────────────
    // Fetch helpers
    // ─────────────────────────────────────────────────────────────────────────
    const fetchNotes = useCallback(async () => {
        if (!user) return;
        setLoading(true);

        let from: string, to: string;
        if (view === 'month') {
            const y = currentDate.getFullYear();
            const m = currentDate.getMonth();
            const firstDay = new Date(y, m, 1);
            const lastDay  = new Date(y, m + 1, 0);
            const gridStart = addDays(firstDay, -trDayIndex(firstDay));
            const gridEnd   = addDays(lastDay,  6 - trDayIndex(lastDay));
            from = toDateStr(gridStart);
            to   = toDateStr(gridEnd);
        } else if (view === 'week') {
            const monday = getMondayOfWeek(currentDate);
            from = toDateStr(monday);
            to   = toDateStr(addDays(monday, 6));
        } else {
            const s = toDateStr(currentDate);
            from = to = s;
        }

        try {
            const res = await axiosInstance.get(
                `/api/KullaniciNotlari/kullanici/${user.kullaniciID}?baslangicTarihi=${from}&bitisTarihi=${to}`
            );
            setNotes(res.data);
        } catch { /* sessiz */ } finally {
            setLoading(false);
        }
    }, [user, view, currentDate]);

    const fetchTodos = useCallback(async () => {
        if (!user) return;
        try {
            const res = await axiosInstance.get(`/api/TodoGorevler?kullaniciID=${user.kullaniciID}`);
            setTodos(res.data);
        } catch { /* sessiz */ }
    }, [user]);

    useEffect(() => { fetchNotes(); }, [fetchNotes]);
    useEffect(() => { fetchTodos(); }, [fetchTodos]);
    useEffect(() => {
        if ((view === 'week' || view === 'day') && timeGridRef.current) {
            timeGridRef.current.scrollTop = 7 * HOUR_HEIGHT - 20;
        }
    }, [view]);

    // ─────────────────────────────────────────────────────────────────────────
    // Navigation
    // ─────────────────────────────────────────────────────────────────────────
    const navigateView = (dir: -1 | 1) => {
        setCurrentDate(prev => {
            const d = new Date(prev);
            if (view === 'month') d.setMonth(d.getMonth() + dir);
            if (view === 'week')  d.setDate(d.getDate() + dir * 7);
            if (view === 'day')   d.setDate(d.getDate() + dir);
            return d;
        });
    };

    const headerTitle = () => {
        if (view === 'month')
            return `${TR_MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
        if (view === 'week') {
            const mon = getMondayOfWeek(currentDate);
            const sun = addDays(mon, 6);
            if (mon.getMonth() === sun.getMonth())
                return `${mon.getDate()} – ${sun.getDate()} ${TR_MONTHS[mon.getMonth()]} ${mon.getFullYear()}`;
            return `${mon.getDate()} ${TR_MONTHS[mon.getMonth()]} – ${sun.getDate()} ${TR_MONTHS[sun.getMonth()]} ${mon.getFullYear()}`;
        }
        return `${TR_DAYS_LONG[trDayIndex(currentDate)]}, ${currentDate.getDate()} ${TR_MONTHS[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Notes CRUD
    // ─────────────────────────────────────────────────────────────────────────
    const openNew = (dateStr: string, hour?: number) => {
        setEditingNote(null);
        setFormDate(dateStr);
        setFormBaslik('');
        setFormIcerik('');
        setFormRenk('primary');
        if (hour !== undefined) {
            setFormTumGun(false);
            setFormBaslangicSaati(minutesToStr(hour * 60));
            setFormBitisSaati(minutesToStr(Math.min(hour * 60 + 60, 23 * 60 + 30)));
        } else {
            setFormTumGun(true);
            setFormBaslangicSaati('');
            setFormBitisSaati('');
        }
        setShowModal(true);
    };

    const openEdit = (note: CalNote, e: React.MouseEvent) => {
        e.stopPropagation();
        setEditingNote(note);
        setFormDate(note.notTarihi.slice(0, 10));
        setFormBaslik(note.notBaslik || '');
        setFormIcerik(note.notIcerik || '');
        setFormRenk(note.notRengi || 'primary');
        const hasSaat = !!note.baslangicSaati;
        setFormTumGun(!hasSaat);
        setFormBaslangicSaati(note.baslangicSaati || '');
        setFormBitisSaati(note.bitisSaati || '');
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingNote(null);
    };

    const saveNote = async () => {
        if (!user || !formBaslik.trim()) return;
        setSaving(true);
        try {
            const baslangic = formTumGun ? null : (formBaslangicSaati || null);
            const bitis     = formTumGun ? null : (formBitisSaati || null);
            if (editingNote) {
                await axiosInstance.put(`/api/KullaniciNotlari/${editingNote.notID}`, {
                    notID: editingNote.notID,
                    kullaniciID: user.kullaniciID,
                    notTarihi: formDate,
                    notBaslik: formBaslik.trim(),
                    notIcerik: formIcerik || null,
                    notRengi: formRenk,
                    aktifMi: true,
                    baslangicSaati: baslangic,
                    bitisSaati: bitis,
                });
                setNotes(prev => prev.map(n =>
                    n.notID === editingNote.notID
                        ? { ...n, notTarihi: formDate, notBaslik: formBaslik.trim(), notIcerik: formIcerik || undefined, notRengi: formRenk, baslangicSaati: baslangic ?? undefined, bitisSaati: bitis ?? undefined }
                        : n
                ));
                toast.success(t('msg.islemBasarili'));
            } else {
                await axiosInstance.post('/api/KullaniciNotlari', {
                    kullaniciID: user.kullaniciID,
                    notTarihi: formDate,
                    notBaslik: formBaslik.trim(),
                    notIcerik: formIcerik || null,
                    notRengi: formRenk,
                    aktifMi: true,
                    baslangicSaati: baslangic,
                    bitisSaati: bitis,
                });
                await fetchNotes();
                toast.success('Etkinlik eklendi');
            }
            closeModal();
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setSaving(false);
        }
    };

    const deleteNote = async (id: number, skipConfirm = false) => {
        if (!skipConfirm && !window.confirm(t('confirm.silmekIstiyormusunuz'))) return;
        try {
            await axiosInstance.delete(`/api/KullaniciNotlari/${id}`);
            setNotes(prev => prev.filter(n => n.notID !== id));
            if (editingNote?.notID === id) closeModal();
            toast.success('Etkinlik silindi');
        } catch {
            toast.error('Silinemedi');
        }
    };

    const moveNote = async (noteId: number, newDateStr: string) => {
        if (!user) return;
        const note = notes.find(n => n.notID === noteId);
        if (!note || note.notTarihi.slice(0, 10) === newDateStr) return;
        setNotes(prev => prev.map(n => n.notID === noteId ? { ...n, notTarihi: newDateStr } : n));
        try {
            await axiosInstance.put(`/api/KullaniciNotlari/${noteId}`, {
                notID: noteId,
                kullaniciID: user!.kullaniciID,
                notTarihi: newDateStr,
                notBaslik: note.notBaslik,
                notIcerik: note.notIcerik || null,
                notRengi: note.notRengi,
                aktifMi: true,
                baslangicSaati: note.baslangicSaati || null,
                bitisSaati: note.bitisSaati || null,
            });
        } catch {
            toast.error(t('msg.islemHatasi'));
            fetchNotes();
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Todo CRUD
    // ─────────────────────────────────────────────────────────────────────────
    const toggleTodo = async (todo: TodoGorev) => {
        const newVal = !todo.tamamlandi;
        setTodos(prev => prev.map(item => item.todoID === todo.todoID ? { ...item, tamamlandi: newVal } : item));
        try {
            await axiosInstance.patch(`/api/TodoGorevler/${todo.todoID}/tamamla`, newVal, {
                headers: { 'Content-Type': 'application/json' }
            });
        } catch {
            setTodos(prev => prev.map(item => item.todoID === todo.todoID ? { ...item, tamamlandi: !newVal } : item));
        }
    };

    const addTodo = async () => {
        if (!user || !yeniTodo.trim()) return;
        setTodoAdding(true);
        try {
            const res = await axiosInstance.post('/api/TodoGorevler', {
                kullaniciID: user.kullaniciID,
                baslik: yeniTodo.trim(),
                renk: '#f8e4d0',
                tamamlandi: false,
                siraNo: todos.length,
                gorevTarihi: yeniTodoTarih || null,
                baslangicSaati: yeniTodoSaat || null,
                bitisSaati: yeniTodoBitis || null,
            });
            setTodos(prev => [...prev, res.data]);
            setYeniTodo('');
            setYeniTodoTarih('');
            setYeniTodoSaat('');
            setYeniTodoBitis('');
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setTodoAdding(false);
        }
    };

    const openTodoEdit = (todo: TodoGorev) => {
        setTodoEditTarget(todo);
        setTodoEditDate(todo.gorevTarihi ?? '');
        setTodoEditSaat(todo.baslangicSaati ?? '');
        setTodoEditBitis(todo.bitisSaati ?? '');
        setTodoEditOpen(true);
    };

    const saveTodoEdit = async () => {
        if (!user || !todoEditTarget) return;
        setTodoEditSaving(true);
        try {
            await axiosInstance.put(`/api/TodoGorevler/${todoEditTarget.todoID}`, {
                kullaniciID: user.kullaniciID,
                baslik: todoEditTarget.baslik,
                renk: todoEditTarget.renk,
                tamamlandi: todoEditTarget.tamamlandi,
                siraNo: todoEditTarget.siraNo,
                gorevTarihi: todoEditDate || null,
                baslangicSaati: todoEditSaat || null,
                bitisSaati: todoEditBitis || null,
            });
            setTodos(prev => prev.map(item =>
                item.todoID === todoEditTarget.todoID
                    ? { ...item, gorevTarihi: todoEditDate || undefined, baslangicSaati: todoEditSaat || undefined, bitisSaati: todoEditBitis || undefined }
                    : item
            ));
            toast.success(t('msg.islemBasarili'));
            setTodoEditOpen(false);
            setTodoEditTarget(null);
        } catch {
            toast.error(t('msg.islemHatasi'));
        } finally {
            setTodoEditSaving(false);
        }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Drag helpers
    // ─────────────────────────────────────────────────────────────────────────
    const handleDragStart = (noteId: number, e: React.DragEvent) => {
        draggedNoteId.current = noteId;
        e.dataTransfer.effectAllowed = 'move';
    };
    const handleDragOver = (dateStr: string, e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverDate(dateStr);
    };
    const handleDrop = (dateStr: string, e: React.DragEvent) => {
        e.preventDefault();
        if (draggedNoteId.current !== null) moveNote(draggedNoteId.current, dateStr);
        draggedNoteId.current = null;
        setDragOverDate(null);
    };
    const handleDragLeave = () => setDragOverDate(null);

    // ─────────────────────────────────────────────────────────────────────────
    // Helpers
    // ─────────────────────────────────────────────────────────────────────────
    const notesForDate = (dateStr: string) =>
        notes.filter(n => n.notTarihi.slice(0, 10) === dateStr);

    const todosForDate   = (dateStr: string) =>
        todos.filter(item => item.gorevTarihi?.slice(0, 10) === dateStr);
    const timedTodosFor  = (dateStr: string) =>
        todosForDate(dateStr).filter(item => item.baslangicSaati);
    const allDayTodosFor = (dateStr: string) =>
        todosForDate(dateStr).filter(item => !item.baslangicSaati);

    // ─────────────────────────────────────────────────────────────────────────
    // Event chip renderer
    // ─────────────────────────────────────────────────────────────────────────
    const renderChip = (note: CalNote, compact: boolean) => {
        const c = getColor(note.notRengi);
        return (
            <div
                key={note.notID}
                draggable
                onDragStart={e => handleDragStart(note.notID, e)}
                onClick={e => openEdit(note, e)}
                style={{
                    background: c.bg,
                    border: `1px solid ${c.border}`,
                    color: c.text,
                    borderRadius: '6px',
                    padding: compact ? '2px 5px' : '3px 8px',
                    fontSize: compact ? '11px' : '12px',
                    fontWeight: 500,
                    lineHeight: '1.45',
                    marginBottom: '2px',
                    cursor: 'grab',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '3px',
                    userSelect: 'none',
                    transition: 'opacity .12s',
                }}
            >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                    {note.baslangicSaati ? <span style={{ opacity: 0.65, marginRight: '2px' }}>{note.baslangicSaati}</span> : null}
                    {note.notBaslik || '(Başlıksız)'}
                </span>
                <button
                    onClick={e => { e.stopPropagation(); deleteNote(note.notID); }}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.text, opacity: 0.55, padding: '0 1px', fontSize: '10px', lineHeight: 1, flexShrink: 0 }}
                >
                    ✕
                </button>
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Todo chip renderer
    // ─────────────────────────────────────────────────────────────────────────
    const renderTodoChip = (todo: TodoGorev, compact: boolean) => (
        <div
            key={`td-${todo.todoID}`}
            data-event="1"
            onClick={e => { e.stopPropagation(); toggleTodo(todo); }}
            style={{
                background: '#f5f3ff',
                border: '1px solid #ddd6fe',
                borderLeft: `3px solid ${todo.renk || '#8b5cf6'}`,
                color: '#5b21b6',
                borderRadius: '6px',
                padding: compact ? '2px 5px' : '3px 8px',
                fontSize: compact ? '11px' : '12px',
                fontWeight: 500,
                lineHeight: '1.45',
                marginBottom: '2px',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                opacity: todo.tamamlandi ? 0.5 : 1,
                transition: 'opacity .12s',
            }}
        >
            <span style={{ fontSize: '10px', flexShrink: 0 }}>{todo.tamamlandi ? '✓' : '☐'}</span>
            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1, textDecoration: todo.tamamlandi ? 'line-through' : 'none' }}>
                {todo.baslangicSaati && <span style={{ opacity: 0.65, marginRight: '2px' }}>{todo.baslangicSaati}</span>}
                {todo.baslik}
            </span>
        </div>
    );

    // ─────────────────────────────────────────────────────────────────────────
    // Month View
    // ─────────────────────────────────────────────────────────────────────────
    const renderMonthView = () => {
        const y = currentDate.getFullYear();
        const m = currentDate.getMonth();
        const firstOfMonth = new Date(y, m, 1);
        const lastOfMonth  = new Date(y, m + 1, 0);
        const startOffset  = trDayIndex(firstOfMonth);
        const gridStart    = addDays(firstOfMonth, -startOffset);
        const totalCells   = Math.ceil((startOffset + lastOfMonth.getDate()) / 7) * 7;
        const cells: Date[] = Array.from({ length: totalCells }, (_, i) => addDays(gridStart, i));
        const MAX_VISIBLE = 3;

        return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Day header */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', borderBottom: '1px solid #e9edf2', flexShrink: 0 }}>
                    {TR_DAYS_SHORT.map((d, i) => (
                        <div key={d} style={{ padding: '8px 0', textAlign: 'center', fontSize: '12px', fontWeight: 600, color: i >= 5 ? '#f97316' : '#64748b', background: '#f8fafc' }}>
                            {d}
                        </div>
                    ))}
                </div>
                {/* Cells */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', flex: 1, overflow: 'auto' }}>
                    {cells.map(cellDate => {
                        const dateStr        = toDateStr(cellDate);
                        const isCurrentMonth = cellDate.getMonth() === m;
                        const isToday        = dateStr === todayStr;
                        const isSelected     = dateStr === toDateStr(currentDate);
                        const isDragOver     = dragOverDate === dateStr;
                        const dayNotes       = notesForDate(dateStr);
                        const cellTodos      = todosForDate(dateStr);
                        const isWeekend      = trDayIndex(cellDate) >= 5;

                        return (
                            <div
                                key={dateStr}
                                onDragOver={e => handleDragOver(dateStr, e)}
                                onDrop={e => handleDrop(dateStr, e)}
                                onDragLeave={handleDragLeave}
                                onClick={() => { setCurrentDate(new Date(cellDate)); }}
                                style={{
                                    border: '1px solid #f1f5f9',
                                    borderTop: 'none',
                                    padding: '5px 5px 6px',
                                    minHeight: '90px',
                                    cursor: 'pointer',
                                    background: isDragOver
                                        ? '#f0f9ff'
                                        : isToday
                                            ? '#fffbf5'
                                            : isWeekend && isCurrentMonth
                                                ? '#fdfcfb'
                                                : 'transparent',
                                    outline: isDragOver
                                        ? '2px solid #3b82f6'
                                        : isSelected
                                            ? '2px solid #cd6906'
                                            : 'none',
                                    outlineOffset: '-2px',
                                    position: 'relative',
                                    transition: 'background .1s',
                                    opacity: isCurrentMonth ? 1 : 0.45,
                                }}
                            >
                                {/* Date number */}
                                <div
                                    onClick={e => { e.stopPropagation(); openNew(dateStr); }}
                                    title="Yeni etkinlik ekle"
                                    style={{
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '12px',
                                        fontWeight: isToday ? 700 : 400,
                                        color: isToday ? '#fff' : isWeekend ? '#f97316' : '#374151',
                                        background: isToday ? '#cd6906' : 'transparent',
                                        marginBottom: '3px',
                                        cursor: 'pointer',
                                        flexShrink: 0,
                                    }}
                                >
                                    {cellDate.getDate()}
                                </div>
                                {/* Events + Todos */}
                                {(() => {
                                    const visNotes = dayNotes.slice(0, MAX_VISIBLE);
                                    const visTodos = cellTodos.slice(0, Math.max(0, MAX_VISIBLE - visNotes.length));
                                    const hidden   = (dayNotes.length - visNotes.length) + (cellTodos.length - visTodos.length);
                                    return (<>
                                        {visNotes.map(n => renderChip(n, true))}
                                        {visTodos.map(td => renderTodoChip(td, true))}
                                        {hidden > 0 && <div style={{ fontSize: '10px', color: '#94a3b8', fontWeight: 500, paddingLeft: '2px' }}>+{hidden} daha</div>}
                                    </>);
                                })()}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Week View  — Google Calendar style time grid
    // ─────────────────────────────────────────────────────────────────────────
    const renderWeekView = () => {
        const monday      = getMondayOfWeek(currentDate);
        const days        = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
        const selectedStr = toDateStr(currentDate);
        const hours       = Array.from({ length: 24 }, (_, i) => i);

        const timedNotesFor  = (dStr: string) => notesForDate(dStr).filter(n => n.baslangicSaati);
        const allDayNotesFor = (dStr: string) => notesForDate(dStr).filter(n => !n.baslangicSaati);

        const handleGridClick = (dStr: string, e: React.MouseEvent<HTMLDivElement>) => {
            if ((e.target as HTMLElement).closest('[data-event]')) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const y    = e.clientY - rect.top;
            const mins = snapTo30(Math.max(0, Math.floor(y / HOUR_HEIGHT * 60)));
            openNew(dStr, Math.floor(mins / 60));
        };

        return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Day header row */}
                <div style={{ display: 'flex', flexShrink: 0, borderBottom: '1px solid #e9edf2', background: '#fff' }}>
                    <div style={{ width: `${TIME_GUTTER}px`, flexShrink: 0 }}></div>
                    {days.map((d, i) => {
                        const dStr     = toDateStr(d);
                        const isTodayD = dStr === todayStr;
                        const isSelD   = dStr === selectedStr;
                        const isWknd   = i >= 5;
                        return (
                            <div key={dStr} onClick={() => setCurrentDate(new Date(d))}
                                style={{ flex: 1, padding: '8px 4px', textAlign: 'center', cursor: 'pointer', borderLeft: '1px solid #f1f5f9', background: isSelD ? '#fff7ed' : 'transparent', borderBottom: isSelD ? '3px solid #cd6906' : '3px solid transparent', transition: 'background .12s' }}>
                                <div style={{ fontSize: '11px', fontWeight: 600, color: isWknd ? '#f97316' : '#64748b', marginBottom: '4px' }}>{TR_DAYS_SHORT[i]}</div>
                                <div style={{ width: '30px', height: '30px', borderRadius: '50%', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: isTodayD ? 700 : 500, color: isTodayD ? '#fff' : isWknd ? '#f97316' : '#0f172a', background: isTodayD ? '#cd6906' : 'transparent' }}>
                                    {d.getDate()}
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* All-day row */}
                <div style={{ display: 'flex', flexShrink: 0, borderBottom: '2px solid #e9edf2', minHeight: '32px' }}>
                    <div style={{ width: `${TIME_GUTTER}px`, flexShrink: 0, fontSize: '10px', color: '#94a3b8', textAlign: 'right', paddingRight: '6px', paddingTop: '8px', boxSizing: 'border-box' }}>tüm gün</div>
                    {days.map(d => {
                        const dStr   = toDateStr(d);
                        const adn    = allDayNotesFor(dStr);
                        const isDnDO = dragOverDate === dStr;
                        return (
                            <div key={dStr}
                                onDragOver={e => handleDragOver(dStr, e)}
                                onDrop={e => handleDrop(dStr, e)}
                                onDragLeave={handleDragLeave}
                                onClick={() => openNew(dStr)}
                                style={{ flex: 1, borderLeft: '1px solid #f1f5f9', padding: '4px 3px', minHeight: '32px', background: isDnDO ? '#f0f9ff' : 'transparent', outline: isDnDO ? '2px solid #3b82f6' : 'none', outlineOffset: '-2px', cursor: 'pointer' }}>
                                {adn.map(n => renderChip(n, true))}
                                {allDayTodosFor(dStr).map(td => renderTodoChip(td, true))}
                            </div>
                        );
                    })}
                </div>

                {/* Scrollable time grid */}
                <div ref={timeGridRef} style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
                    <div style={{ position: 'relative', height: `${24 * HOUR_HEIGHT}px` }}>
                        {/* Hour lines + labels */}
                        {hours.map(h => (
                            <div key={h} style={{ position: 'absolute', left: 0, right: 0, top: `${h * HOUR_HEIGHT}px`, display: 'flex', alignItems: 'flex-start', pointerEvents: 'none', zIndex: 1 }}>
                                <div style={{ width: `${TIME_GUTTER}px`, fontSize: '11px', color: '#94a3b8', textAlign: 'right', paddingRight: '8px', marginTop: '-7px', flexShrink: 0, lineHeight: '14px' }}>
                                    {h > 0 ? `${String(h).padStart(2, '0')}:00` : ''}
                                </div>
                                <div style={{ flex: 1, borderTop: h === 0 ? 'none' : '1px solid #f1f5f9' }}></div>
                            </div>
                        ))}
                        {/* Half-hour dashed lines */}
                        {hours.map(h => (
                            <div key={`hh${h}`} style={{ position: 'absolute', left: `${TIME_GUTTER}px`, right: 0, top: `${h * HOUR_HEIGHT + HOUR_HEIGHT / 2}px`, borderTop: '1px dashed #f4f6f9', pointerEvents: 'none', zIndex: 1 }}></div>
                        ))}
                        {/* Day columns */}
                        <div style={{ position: 'absolute', left: `${TIME_GUTTER}px`, top: 0, right: 0, bottom: 0, display: 'flex' }}>
                            {days.map((d, i) => {
                                const dStr   = toDateStr(d);
                                const isTodC = dStr === todayStr;
                                const dn     = timedNotesFor(dStr);
                                return (
                                    <div key={dStr}
                                        onClick={e => handleGridClick(dStr, e)}
                                        style={{ flex: 1, position: 'relative', borderLeft: i === 0 ? 'none' : '1px solid #f1f5f9', background: isTodC ? '#fffdf9' : 'transparent', cursor: 'crosshair' }}>
                                        {/* Timed events */}
                                        {dn.map(note => {
                                            const start = parseTime(note.baslangicSaati!);
                                            const end   = note.bitisSaati ? parseTime(note.bitisSaati) : start + 60;
                                            const topPx = (start / 60) * HOUR_HEIGHT;
                                            const hgtPx = Math.max(((end - start) / 60) * HOUR_HEIGHT, 22);
                                            const c     = getColor(note.notRengi);
                                            return (
                                                <div key={note.notID}
                                                    data-event="1"
                                                    draggable
                                                    onDragStart={e => { e.stopPropagation(); handleDragStart(note.notID, e); }}
                                                    onClick={e => openEdit(note, e)}
                                                    style={{ position: 'absolute', left: '2px', right: '2px', top: `${topPx}px`, height: `${hgtPx}px`, background: c.bg, border: `1px solid ${c.border}`, borderLeft: `3px solid ${c.border}`, borderRadius: '5px', padding: '3px 5px', fontSize: '11px', fontWeight: 500, color: c.text, overflow: 'hidden', cursor: 'pointer', zIndex: 2, boxSizing: 'border-box', boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
                                                    <div style={{ fontWeight: 600, lineHeight: '1.3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{note.notBaslik || '(Başlıksız)'}</div>
                                                    {hgtPx > 32 && <div style={{ fontSize: '10px', opacity: 0.75, lineHeight: '1.2' }}>{note.baslangicSaati}{note.bitisSaati ? ` – ${note.bitisSaati}` : ''}</div>}
                                                </div>
                                            );
                                        })}
                                        {/* Timed todos */}
                                        {timedTodosFor(dStr).map(todo => {
                                            const tStart = parseTime(todo.baslangicSaati!);
                                            const tEnd   = todo.bitisSaati ? parseTime(todo.bitisSaati) : tStart + 30;
                                            const tTop   = (tStart / 60) * HOUR_HEIGHT;
                                            const tHgt   = Math.max(((tEnd - tStart) / 60) * HOUR_HEIGHT, 22);
                                            return (
                                                <div key={`td${todo.todoID}`} data-event="1"
                                                    onClick={e => { e.stopPropagation(); toggleTodo(todo); }}
                                                    style={{ position: 'absolute', left: '2px', right: '2px', top: `${tTop}px`, height: `${tHgt}px`, background: '#f5f3ff', border: '1px solid #ddd6fe', borderLeft: `3px solid ${todo.renk || '#8b5cf6'}`, borderRadius: '5px', padding: '3px 5px', fontSize: '11px', fontWeight: 500, color: '#5b21b6', overflow: 'hidden', cursor: 'pointer', zIndex: 2, boxSizing: 'border-box', opacity: todo.tamamlandi ? 0.5 : 1 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                                        <span style={{ flexShrink: 0 }}>{todo.tamamlandi ? '✓' : '☐'}</span>
                                                        <span style={{ fontWeight: 600, lineHeight: '1.3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: todo.tamamlandi ? 'line-through' : 'none' }}>{todo.baslik}</span>
                                                    </div>
                                                    {tHgt > 32 && <div style={{ fontSize: '10px', opacity: 0.75 }}>{todo.baslangicSaati}{todo.bitisSaati ? ` – ${todo.bitisSaati}` : ''}</div>}
                                                </div>
                                            );
                                        })}
                                        {/* Current time indicator */}
                                        {isTodC && (() => {
                                            const now = new Date();
                                            const top = (now.getHours() * 60 + now.getMinutes()) / 60 * HOUR_HEIGHT;
                                            return (
                                                <div style={{ position: 'absolute', left: '-4px', right: 0, top: `${top}px`, zIndex: 3, pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
                                                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', flexShrink: 0 }}></div>
                                                    <div style={{ flex: 1, height: '2px', background: '#ef4444' }}></div>
                                                </div>
                                            );
                                        })()}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Day View  — single-column time grid
    // ─────────────────────────────────────────────────────────────────────────
    const renderDayView = () => {
        const dStr       = toDateStr(currentDate);
        const hours      = Array.from({ length: 24 }, (_, i) => i);
        const dn         = notesForDate(dStr).filter(n => n.baslangicSaati);
        const allDayN    = notesForDate(dStr).filter(n => !n.baslangicSaati);
        const isDragO    = dragOverDate === dStr;
        const isTodayDay = dStr === todayStr;

        const handleGridClick = (e: React.MouseEvent<HTMLDivElement>) => {
            if ((e.target as HTMLElement).closest('[data-event]')) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const y    = e.clientY - rect.top;
            const mins = snapTo30(Math.max(0, Math.floor(y / HOUR_HEIGHT * 60)));
            openNew(dStr, Math.floor(mins / 60));
        };

        return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                {/* Day header */}
                <div style={{ display: 'flex', flexShrink: 0, borderBottom: '1px solid #e9edf2', background: '#fff', alignItems: 'center' }}>
                    <div style={{ width: `${TIME_GUTTER}px`, flexShrink: 0 }}></div>
                    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px 10px 0' }}>
                        <div>
                            <h2 style={{ fontSize: '17px', fontWeight: 700, color: '#0f172a', margin: 0 }}>{TR_DAYS_LONG[trDayIndex(currentDate)]}</h2>
                            <p style={{ fontSize: '12px', color: '#64748b', margin: '2px 0 0' }}>{currentDate.getDate()} {TR_MONTHS[currentDate.getMonth()]} {currentDate.getFullYear()}</p>
                        </div>
                        <button onClick={() => openNew(dStr)} style={{ padding: '7px 14px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: '9px', fontSize: '12px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                            Yeni
                        </button>
                    </div>
                </div>

                {/* All-day row */}
                {(allDayN.length > 0 || allDayTodosFor(dStr).length > 0) && (
                    <div style={{ display: 'flex', flexShrink: 0, borderBottom: '2px solid #e9edf2', minHeight: '32px' }}>
                        <div style={{ width: `${TIME_GUTTER}px`, flexShrink: 0, fontSize: '10px', color: '#94a3b8', textAlign: 'right', paddingRight: '6px', paddingTop: '8px', boxSizing: 'border-box' }}>tüm gün</div>
                        <div onDragOver={e => handleDragOver(dStr, e)} onDrop={e => handleDrop(dStr, e)} onDragLeave={handleDragLeave} onClick={() => openNew(dStr)}
                            style={{ flex: 1, borderLeft: '1px solid #f1f5f9', padding: '4px 8px', background: isDragO ? '#f0f9ff' : 'transparent', cursor: 'pointer' }}>
                            {allDayN.map(n => renderChip(n, false))}
                            {allDayTodosFor(dStr).map(item => renderTodoChip(t, false))}
                        </div>
                    </div>
                )}

                {/* Scrollable time grid */}
                <div ref={timeGridRef} style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
                    <div style={{ position: 'relative', height: `${24 * HOUR_HEIGHT}px` }}>
                        {/* Hour lines + labels */}
                        {hours.map(h => (
                            <div key={h} style={{ position: 'absolute', left: 0, right: 0, top: `${h * HOUR_HEIGHT}px`, display: 'flex', alignItems: 'flex-start', pointerEvents: 'none', zIndex: 1 }}>
                                <div style={{ width: `${TIME_GUTTER}px`, fontSize: '11px', color: '#94a3b8', textAlign: 'right', paddingRight: '8px', marginTop: '-7px', flexShrink: 0, lineHeight: '14px' }}>
                                    {h > 0 ? `${String(h).padStart(2, '0')}:00` : ''}
                                </div>
                                <div style={{ flex: 1, borderTop: h === 0 ? 'none' : '1px solid #f1f5f9' }}></div>
                            </div>
                        ))}
                        {hours.map(h => (
                            <div key={`hh${h}`} style={{ position: 'absolute', left: `${TIME_GUTTER}px`, right: 0, top: `${h * HOUR_HEIGHT + HOUR_HEIGHT / 2}px`, borderTop: '1px dashed #f4f6f9', pointerEvents: 'none', zIndex: 1 }}></div>
                        ))}
                        {/* Day column */}
                        <div style={{ position: 'absolute', left: `${TIME_GUTTER}px`, top: 0, right: 0, bottom: 0 }}>
                            <div onClick={handleGridClick}
                                style={{ position: 'relative', height: '100%', background: isTodayDay ? '#fffdf9' : 'transparent', cursor: 'crosshair' }}>
                                {dn.map(note => {
                                    const start = parseTime(note.baslangicSaati!);
                                    const end   = note.bitisSaati ? parseTime(note.bitisSaati) : start + 60;
                                    const topPx = (start / 60) * HOUR_HEIGHT;
                                    const hgtPx = Math.max(((end - start) / 60) * HOUR_HEIGHT, 22);
                                    const c     = getColor(note.notRengi);
                                    return (
                                        <div key={note.notID}
                                            data-event="1"
                                            draggable
                                            onDragStart={e => handleDragStart(note.notID, e)}
                                            onClick={e => openEdit(note, e)}
                                            style={{ position: 'absolute', left: '8px', right: '8px', top: `${topPx}px`, height: `${hgtPx}px`, background: c.bg, border: `1px solid ${c.border}`, borderLeft: `4px solid ${c.border}`, borderRadius: '8px', padding: '4px 10px', fontSize: '12px', fontWeight: 500, color: c.text, overflow: 'hidden', cursor: 'pointer', zIndex: 2, boxSizing: 'border-box', boxShadow: '0 1px 3px rgba(0,0,0,.08)' }}>
                                            <div style={{ fontWeight: 600, fontSize: '13px', lineHeight: '1.3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{note.notBaslik || '(Başlıksız)'}</div>
                                            {hgtPx > 34 && <div style={{ fontSize: '11px', opacity: 0.75, marginTop: '2px' }}>{note.baslangicSaati}{note.bitisSaati ? ` – ${note.bitisSaati}` : ''}</div>}
                                            {hgtPx > 56 && note.notIcerik && <div style={{ fontSize: '11px', opacity: 0.6, marginTop: '2px', lineHeight: '1.4' }}>{note.notIcerik}</div>}
                                        </div>
                                    );
                                })}
                                {/* Timed todos */}
                                {timedTodosFor(dStr).map(todo => {
                                    const tStart = parseTime(todo.baslangicSaati!);
                                    const tEnd   = todo.bitisSaati ? parseTime(todo.bitisSaati) : tStart + 30;
                                    const tTop   = (tStart / 60) * HOUR_HEIGHT;
                                    const tHgt   = Math.max(((tEnd - tStart) / 60) * HOUR_HEIGHT, 22);
                                    return (
                                        <div key={`td${todo.todoID}`} data-event="1"
                                            onClick={e => { e.stopPropagation(); toggleTodo(todo); }}
                                            style={{ position: 'absolute', left: '8px', right: '8px', top: `${tTop}px`, height: `${tHgt}px`, background: '#f5f3ff', border: '1px solid #ddd6fe', borderLeft: `4px solid ${todo.renk || '#8b5cf6'}`, borderRadius: '8px', padding: '4px 10px', fontSize: '12px', fontWeight: 500, color: '#5b21b6', overflow: 'hidden', cursor: 'pointer', zIndex: 2, boxSizing: 'border-box', opacity: todo.tamamlandi ? 0.5 : 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                <span style={{ flexShrink: 0, fontSize: '12px' }}>{todo.tamamlandi ? '✓' : '☐'}</span>
                                                <span style={{ fontWeight: 600, fontSize: '13px', lineHeight: '1.3', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: todo.tamamlandi ? 'line-through' : 'none' }}>{todo.baslik}</span>
                                            </div>
                                            {tHgt > 34 && <div style={{ fontSize: '11px', opacity: 0.75, marginTop: '2px' }}>{todo.baslangicSaati}{todo.bitisSaati ? ` – ${todo.bitisSaati}` : ''}</div>}
                                        </div>
                                    );
                                })}
                                {/* Current time indicator */}
                                {isTodayDay && (() => {
                                    const now = new Date();
                                    const top = (now.getHours() * 60 + now.getMinutes()) / 60 * HOUR_HEIGHT;
                                    return (
                                        <div style={{ position: 'absolute', left: '-4px', right: 0, top: `${top}px`, zIndex: 3, pointerEvents: 'none', display: 'flex', alignItems: 'center' }}>
                                            <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#ef4444', flexShrink: 0 }}></div>
                                            <div style={{ flex: 1, height: '2px', background: '#ef4444' }}></div>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Sidebar (Todos)
    // ─────────────────────────────────────────────────────────────────────────
    const renderSidebar = () => {
        const pending = todos.filter(item => !item.tamamlandi);
        const done    = todos.filter(item => item.tamamlandi);

        return (
            <div style={{ width: '272px', flexShrink: 0, borderLeft: '1px solid #e9edf2', display: 'flex', flexDirection: 'column', background: '#fafbfc' }}>
                {/* Header + add input */}
                <div style={{ padding: '14px 16px 12px', borderBottom: '1px solid #e9edf2', background: '#fff' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                        <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#8b5cf6', display: 'inline-block' }}></span>
                            Görevlerim
                        </div>
                        <span style={{ fontSize: '11px', fontWeight: 500, padding: '2px 8px', borderRadius: '20px', background: '#f5f3ff', color: '#6d28d9' }}>
                            {pending.length} bekliyor
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: '6px' }}>
                        <input
                            type="text"
                            value={yeniTodo}
                            onChange={e => setYeniTodo(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && addTodo()}
                            placeholder={t('label.ara')}
                            style={{ flex: 1, padding: '6px 10px', border: '1px solid #e9edf2', borderRadius: '8px', fontSize: '12px', outline: 'none', color: '#374151' }}
                        />
                        <button
                            onClick={addTodo}
                            disabled={todoAdding || !yeniTodo.trim()}
                            style={{ padding: '6px 10px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: 700, opacity: (!yeniTodo.trim() || todoAdding) ? .5 : 1 }}
                        >
                            +
                        </button>
                    </div>
                    <div style={{ display: 'flex', gap: '6px', marginTop: '6px' }}>
                        <TrDateInput
                            value={yeniTodoTarih}
                            onChange={e => setYeniTodoTarih(e.target.value)}
                            title="Görev tarihi (opsiyonel)"
                            style={{ flex: 1, padding: '5px 8px', border: '1px solid #e9edf2', borderRadius: '8px', fontSize: '11px', outline: 'none', color: '#374151', background: '#fafbfc' }}
                        />
                        <input
                            type="time"
                            value={yeniTodoSaat}
                            onChange={e => setYeniTodoSaat(e.target.value)}
                            title={t('label.baslangicTarihi')}
                            style={{ width: '84px', padding: '5px 6px', border: '1px solid #e9edf2', borderRadius: '8px', fontSize: '11px', outline: 'none', color: '#374151', background: '#fafbfc' }}
                        />
                    </div>
                </div>

                {/* Todo list */}
                <div style={{ flex: 1, overflow: 'auto', padding: '6px 0' }}>
                    {todos.length === 0 ? (
                        <div style={{ padding: '32px 16px', textAlign: 'center', color: '#94a3b8', fontSize: '12px' }}>
                            Henüz görev yok.
                        </div>
                    ) : (
                        <>
                            {pending.map(todo => (
                                <div
                                    key={todo.todoID}
                                    style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '9px 16px', borderBottom: '1px solid #f4f6f8', transition: 'background .1s' }}
                                >
                                    <div
                                        onClick={() => toggleTodo(todo)}
                                        style={{ width: '16px', height: '16px', borderRadius: '5px', border: '2px solid #d1d5db', background: '#fff', flexShrink: 0, marginTop: '1px', cursor: 'pointer' }}
                                    />
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <span style={{ fontSize: '12px', color: '#374151', lineHeight: '1.4', display: 'block' }}>
                                            {todo.baslik}
                                        </span>
                                        {todo.gorevTarihi && (
                                            <div style={{ fontSize: '10px', color: '#8b5cf6', marginTop: '2px' }}>
                                                📅 {formatDateTR(todo.gorevTarihi)}{todo.baslangicSaati ? ` · ${todo.baslangicSaati}${todo.bitisSaati ? ` – ${todo.bitisSaati}` : ''}` : ''}
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        onClick={e => { e.stopPropagation(); openTodoEdit(todo); }}
                                        title={t('btn.duzenle')}
                                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: '0 2px', fontSize: '12px', flexShrink: 0, lineHeight: 1, marginTop: '1px' }}
                                    >
                                        ✏️
                                    </button>
                                </div>
                            ))}
                            {done.length > 0 && (
                                <>
                                    <div style={{ padding: '10px 16px 4px', fontSize: '10px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '.5px' }}>
                                        Tamamlananlar ({done.length})
                                    </div>
                                    {done.map(todo => (
                                        <div
                                            key={todo.todoID}
                                            onClick={() => toggleTodo(todo)}
                                            style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '9px 16px', cursor: 'pointer', borderBottom: '1px solid #f4f6f8', opacity: 0.5 }}
                                        >
                                            <div style={{ width: '16px', height: '16px', borderRadius: '5px', border: '2px solid #cd6906', background: '#cd6906', flexShrink: 0, marginTop: '1px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                                            </div>
                                            <span style={{ fontSize: '12px', color: '#374151', lineHeight: '1.4', flex: 1, textDecoration: 'line-through' }}>
                                                {todo.baslik}
                                            </span>
                                        </div>
                                    ))}
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* Today's note count */}
                <div style={{ padding: '10px 16px', borderTop: '1px solid #e9edf2', fontSize: '11px', color: '#94a3b8' }}>
                    Bugün {notesForDate(todayStr).length} etkinlik
                </div>
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Modal
    // ─────────────────────────────────────────────────────────────────────────
    const renderModal = () => {
        if (!showModal) return null;
        return (
            <div
                style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)' }}
                onClick={closeModal}
            >
                <div
                    style={{ background: '#fff', borderRadius: '18px', padding: '28px', width: '440px', maxWidth: '92vw', boxShadow: '0 24px 64px rgba(15,23,42,.2)', animation: 'fadeUp .18s ease' }}
                    onClick={e => e.stopPropagation()}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 700, color: '#0f172a' }}>
                            {editingNote ? 'Etkinliği Düzenle' : 'Yeni Etkinlik'}
                        </h3>
                        <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '18px', padding: '4px', lineHeight: 1 }}>
                            ✕
                        </button>
                    </div>

                    {/* Tarih */}
                    <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Tarih</label>
                        <TrDateInput
                            value={formDate}
                            onChange={e => setFormDate(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #e9edf2', borderRadius: '10px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', color: '#374151' }}
                        />
                    </div>

                    {/* Tüm gün toggle */}
                    <div style={{ marginBottom: '12px' }}>
                        <label style={{ fontSize: '12px', fontWeight: 600, color: '#374151', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', userSelect: 'none' }}>
                            <input
                                type="checkbox"
                                checked={formTumGun}
                                onChange={e => {
                                    const checked = e.target.checked;
                                    setFormTumGun(checked);
                                    if (!checked && !formBaslangicSaati) {
                                        setFormBaslangicSaati('09:00');
                                        setFormBitisSaati('10:00');
                                    }
                                }}
                                style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#cd6906' }}
                            />
                            Tüm gün etkinlik
                        </label>
                    </div>

                    {/* Saat alanları */}
                    {!formTumGun && (
                        <div style={{ marginBottom: '14px', display: 'flex', gap: '12px' }}>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Başlangıç saati</label>
                                <input
                                    type="time"
                                    value={formBaslangicSaati}
                                    onChange={e => setFormBaslangicSaati(e.target.value)}
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #e9edf2', borderRadius: '10px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', color: '#374151' }}
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Bitiş saati</label>
                                <input
                                    type="time"
                                    value={formBitisSaati}
                                    onChange={e => setFormBitisSaati(e.target.value)}
                                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #e9edf2', borderRadius: '10px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', color: '#374151' }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Başlık */}
                    <div style={{ marginBottom: '14px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>
                            Başlık <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input
                            type="text"
                            value={formBaslik}
                            onChange={e => setFormBaslik(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && saveNote()}
                            placeholder={t('label.ara')}
                            autoFocus
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #e9edf2', borderRadius: '10px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', color: '#374151' }}
                        />
                    </div>

                    {/* İçerik */}
                    <div style={{ marginBottom: '14px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Not (isteğe bağlı)</label>
                        <textarea
                            value={formIcerik}
                            onChange={e => setFormIcerik(e.target.value)}
                            placeholder={t('label.aciklama')}
                            rows={3}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #e9edf2', borderRadius: '10px', fontSize: '13px', outline: 'none', resize: 'vertical', boxSizing: 'border-box', color: '#374151', fontFamily: 'inherit' }}
                        />
                    </div>

                    {/* Renk */}
                    <div style={{ marginBottom: '22px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>Renk</label>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                            {COLOR_OPTIONS.map(c => (
                                <div
                                    key={c.value}
                                    onClick={() => setFormRenk(c.value)}
                                    title={c.label}
                                    style={{
                                        width: '30px', height: '30px', borderRadius: '50%',
                                        background: c.bg,
                                        border: formRenk === c.value ? `3px solid ${c.border}` : '2px solid #e9edf2',
                                        boxShadow: formRenk === c.value ? `0 0 0 2px ${c.border}50` : 'none',
                                        cursor: 'pointer',
                                        transition: 'all .15s',
                                    }}
                                />
                            ))}
                        </div>
                    </div>

                    {/* Buttons */}
                    <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
                        {editingNote && (
                            <button
                                onClick={() => { closeModal(); deleteNote(editingNote!.notID, true); }}
                                style={{ padding: '9px 16px', background: '#fde8e8', color: '#991b1b', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                            >
                                Sil
                            </button>
                        )}
                        <button
                            onClick={closeModal}
                            style={{ padding: '9px 16px', background: '#f1f5f9', color: '#374151', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
                        >
                            İptal
                        </button>
                        <button
                            onClick={saveNote}
                            disabled={saving || !formBaslik.trim()}
                            style={{ padding: '9px 18px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '13px', fontWeight: 600, opacity: (saving || !formBaslik.trim()) ? .6 : 1 }}
                        >
                            {saving ? 'Kaydediliyor…' : editingNote ? 'Güncelle' : 'Kaydet'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Todo Edit Modal
    // ─────────────────────────────────────────────────────────────────────────
    const renderTodoEditModal = () => {
        if (!todoEditOpen || !todoEditTarget) return null;
        return (
            <div
                style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,.4)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)' }}
                onClick={() => { setTodoEditOpen(false); setTodoEditTarget(null); }}
            >
                <div
                    style={{ background: '#fff', borderRadius: '18px', padding: '24px', width: '360px', maxWidth: '92vw', boxShadow: '0 24px 64px rgba(15,23,42,.2)', animation: 'fadeUp .18s ease' }}
                    onClick={e => e.stopPropagation()}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
                        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a' }}>Görev Tarihi / Saati</h3>
                        <button onClick={() => { setTodoEditOpen(false); setTodoEditTarget(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: '18px', padding: '4px', lineHeight: 1 }}>✕</button>
                    </div>
                    <p style={{ fontSize: '12px', color: '#64748b', margin: '0 0 14px', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>{todoEditTarget.baslik}</p>

                    <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Tarih</label>
                        <TrDateInput value={todoEditDate} onChange={e => setTodoEditDate(e.target.value)}
                            style={{ width: '100%', padding: '8px 12px', border: '1px solid #e9edf2', borderRadius: '10px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', color: '#374151' }} />
                    </div>

                    <div style={{ display: 'flex', gap: '12px', marginBottom: '18px' }}>
                        <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Başlangıç saati</label>
                            <input type="time" value={todoEditSaat} onChange={e => setTodoEditSaat(e.target.value)}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e9edf2', borderRadius: '10px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', color: '#374151' }} />
                        </div>
                        <div style={{ flex: 1 }}>
                            <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: '#374151', marginBottom: '6px' }}>Bitiş saati</label>
                            <input type="time" value={todoEditBitis} onChange={e => setTodoEditBitis(e.target.value)}
                                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e9edf2', borderRadius: '10px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', color: '#374151' }} />
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                        <button onClick={() => { setTodoEditOpen(false); setTodoEditTarget(null); }}
                            style={{ padding: '8px 16px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: 500 }}>
                            İptal
                        </button>
                        <button onClick={saveTodoEdit} disabled={todoEditSaving}
                            style={{ padding: '8px 20px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: 600, opacity: todoEditSaving ? .6 : 1 }}>
                            {todoEditSaving ? '…' : 'Kaydet'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    // ─────────────────────────────────────────────────────────────────────────
    // Main Render
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            <style>{`
                @keyframes fadeUp { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
                .aj-wrap { display:flex; flex-direction:column; height:calc(100vh - 68px); overflow:hidden; background:#fff; }
                .aj-header { display:flex; align-items:center; gap:12px; padding:12px 20px; border-bottom:1px solid #e9edf2; flex-shrink:0; flex-wrap:wrap; background:#fff; }
                .aj-nav-btn { width:30px; height:30px; border:1px solid #e9edf2; background:#fff; border-radius:8px; cursor:pointer; display:flex; align-items:center; justify-content:center; color:#374151; transition:all .15s; }
                .aj-nav-btn:hover { background:#f1f5f9; }
                .aj-today-btn { padding:5px 14px; border:1px solid #e9edf2; background:#fff; border-radius:8px; font-size:13px; font-weight:600; color:#374151; cursor:pointer; transition:all .15s; }
                .aj-today-btn:hover { background:#f1f5f9; }
                .aj-view-btn { padding:5px 13px; border:1px solid transparent; background:transparent; border-radius:8px; font-size:13px; font-weight:500; color:#64748b; cursor:pointer; transition:all .15s; }
                .aj-view-btn:hover { background:#f1f5f9; }
                .aj-view-btn.active { background:#fff7ed; color:#cd6906; border-color:#fed7aa; font-weight:600; }
                .aj-body { display:flex; flex:1; overflow:hidden; }
                .aj-calendar { flex:1; overflow:hidden; display:flex; flex-direction:column; }
            `}</style>

            <div className="aj-wrap">
                {/* ─── Header ─── */}
                <div className="aj-header">
                    <div style={{ display: 'flex', gap: '4px' }}>
                        <button className="aj-nav-btn" onClick={() => navigateView(-1)} title="Önceki">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                        </button>
                        <button className="aj-nav-btn" onClick={() => navigateView(1)} title="Sonraki">
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="9 18 15 12 9 6"/></svg>
                        </button>
                    </div>

                    <button className="aj-today-btn" onClick={() => setCurrentDate(new Date(todayBase))}>
                        Bugün
                    </button>

                    <h2 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: '#0f172a', flex: 1, minWidth: 120 }}>
                        {headerTitle()}
                    </h2>

                    <button
                        onClick={() => openNew(toDateStr(currentDate))}
                        style={{ padding: '7px 15px', background: '#cd6906', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', whiteSpace: 'nowrap' }}
                    >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
                        Yeni Etkinlik
                    </button>

                    <div style={{ display: 'flex', gap: '2px', background: '#f8fafc', borderRadius: '10px', padding: '3px' }}>
                        {(['month', 'week', 'day'] as ViewType[]).map(v => (
                            <button
                                key={v}
                                className={`aj-view-btn${view === v ? ' active' : ''}`}
                                onClick={() => setView(v)}
                            >
                                {v === 'month' ? 'Ay' : v === 'week' ? 'Hafta' : 'Gün'}
                            </button>
                        ))}
                    </div>
                </div>

                {/* ─── Body ─── */}
                <div className="aj-body">
                    <div className="aj-calendar">
                        {view === 'month' && renderMonthView()}
                        {view === 'week'  && renderWeekView()}
                        {view === 'day'   && renderDayView()}
                    </div>
                    {renderSidebar()}
                </div>
            </div>

            {renderModal()}
            {renderTodoEditModal()}
        </>
    );
};

export default Ajanda;
